/**
 * One proposed role, with its three verdicts.
 *
 * The card shows the same facts whoever is reading it. What changes with the viewer is only whether
 * the decision controls render — see `ApprovalControls`, and note that the real check is on the
 * proxy. Transparency is the point of this page: the proposer, the People team and the approvers
 * are all looking at one row, so "where has my role got to?" has one answer rather than three.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type {
  ApprovalDecision,
  ApproverKey,
  ApprovalViewer,
  PriorityRating,
  RoleProposal,
} from '../../types/roleApprovals';
import { CompensationSummary } from '../../components/CompensationSummary';
import { ApprovalControls } from './ApprovalControls';
import { PriorityPanel } from './PriorityPanel';
import { DecisionPill, Fact, RADIUS, StatusPill, shortDate } from './approvalUi';

interface Props {
  proposal: RoleProposal;
  viewer: ApprovalViewer;
  busy: boolean;
  onDecide: (
    proposalId: string,
    approverKey: ApproverKey,
    decision: ApprovalDecision,
    reason?: string,
    priorityRating?: PriorityRating | null
  ) => void;
  onRetryTransfer: (proposalId: string) => void;
  onSetPriority: (proposalId: string, rating: PriorityRating | null) => void;
  /** The CEO's name, for "awaiting …" beside an unapproved rating. */
  priorityOwner: string;
  /**
   * Set when this role is one of several filed together, and rendered inside `ProposalGroupCard`.
   * The card then leaves out everything the roles share — the group card says it once — and
   * leads with what tells them apart: which role this is and its level.
   */
  member?: { index: number; total: number };
  /**
   * The CEO's not-yet-saved rating pick, held by the GROUP card when this role is one of several —
   * so "Approve all" can send each role's pick instead of silently approving the old rating.
   * Uncontrolled (the card's own state) when absent.
   */
  pickedRating?: PriorityRating | null;
  onPickedRatingChange?: (rating: PriorityRating | null | undefined) => void;
}

/** The office whose holder owns the rating — mirrors `PRIORITY_OWNER_KEY` in the proxy. */
const PRIORITY_OWNER_KEY: ApproverKey = 'ceo';

/**
 * One proposed role: its facts, the three sign-offs, and the controls on every row the viewer signs
 * for (`viewer.approverKeys`, more than one for a multi-seat delegate).
 */
export function ProposalCard({
  proposal,
  viewer,
  busy,
  onDecide,
  onRetryTransfer,
  onSetPriority,
  priorityOwner,
  member,
  pickedRating: controlledPick,
  onPickedRatingChange,
}: Props) {
  const [showLog, setShowLog] = useState(false);
  const priority = proposal.priority ?? { suggested: null, approved: null };
  // `undefined` = untouched, so the select follows the row as it refetches: approved, else the
  // suggestion. Anything the CEO picks sticks until he saves it or approves with it.
  const [ownPick, setOwnPick] = useState<PriorityRating | null | undefined>(undefined);
  const controlled = onPickedRatingChange !== undefined;
  const pickedRating = controlled ? controlledPick : ownPick;
  const setPickedRating = controlled ? onPickedRatingChange : setOwnPick;
  const draftRating = pickedRating !== undefined ? pickedRating : (priority.approved ?? priority.suggested);

  const transferred = Boolean(proposal.hiringPlanRecordId);
  const locked = transferred || proposal.status === 'Cancelled';
  // Approved but NOT copied across: the copy is what puts the role in the hiring plan, so a role
  // stuck here is approved on paper and invisible to everyone planning against the plan. Said
  // loudly rather than left to be noticed.
  const transferPending = proposal.status === 'Approved' && !transferred;
  const canFinishTransfer = viewer.approverKeys.length > 0 || viewer.isAdmin;

  return (
    <article
      className={
        member
          ? `${RADIUS} border border-zinc-200 bg-zinc-50/60 p-3`
          : `${RADIUS} border border-zinc-200 bg-white p-4`
      }
      aria-label={member ? `Role ${member.index + 1} of ${member.total}` : undefined}
    >
      <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
        {member ? (
          // The level leads: it is what tells two otherwise identical roles apart, and the reason
          // a bulk role used to be declined as a duplicate of its sibling.
          <h3 className="text-sm font-semibold text-zinc-900">
            Role {member.index + 1} of {member.total} · {proposal.trackAndLevel || 'no level set'}
          </h3>
        ) : (
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">{proposal.title}</h2>
            <p className="text-xs text-zinc-500">
              {[proposal.peopleTeam, proposal.role].filter(Boolean).join(' · ') || 'No department set'}
            </p>
          </div>
        )}
        <StatusPill status={proposal.status} />
      </header>

      {member ? (
        <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
          <Fact label="Planned start" value={proposal.plannedStartDate} />
        </div>
      ) : (
        <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
          <Fact label="Hiring manager" value={proposal.hiringManager} />
          <Fact label="Planned start" value={proposal.plannedStartDate} />
          <Fact label="Track & level" value={proposal.trackAndLevel} />
          <Fact label="Unit" value={proposal.unit} />
          <Fact label="Reason" value={proposal.reasonForHiring} />
          <Fact label="Cost category" value={proposal.costCategory} />
          <Fact label="Proposed by" value={proposal.proposedBy} />
          <Fact label="Proposed on" value={shortDate(proposal.createdOn)} />
        </div>
      )}

      {/* Above the rationale, not below it: this is a decision about money, and the two figures
          should be read before the argument for the role rather than after it. */}
      <CompensationSummary compensation={proposal.compensation} className="mb-3" />

      {/* Beside the rationale that argues for it, above the sign-offs that approve it. */}
      <PriorityPanel
        priority={priority}
        draft={draftRating}
        onDraftChange={setPickedRating}
        // A rating a former CEO set stays theirs (`approvedByName`); the proxy refuses a change.
        editable={Boolean(viewer.canSetPriority) && !locked && !priority.approvedByName}
        ceoApproved={proposal.approvals.some((a) => a.key === PRIORITY_OWNER_KEY && a.decision === 'Approved')}
        ownerName={priorityOwner}
        busy={busy}
        onSave={(rating) => {
          onSetPriority(proposal.id, rating);
          setPickedRating(undefined);
        }}
      />

      {proposal.rationale && !member && (
        <p className={`${RADIUS} mb-3 border-l-2 border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700`}>
          {proposal.rationale}
        </p>
      )}

      <div className="flex flex-col gap-3 border-t border-zinc-100 pt-3">
        {proposal.approvals.map((approver) => {
          const isSelf = viewer.approverKeys.includes(approver.key);
          // A former holder's decision is theirs and stays as it is: no controls on it.
          const canDecide = !locked && !approver.decidedByName && (isSelf || viewer.isAdmin);
          return (
            <div key={approver.key} className="flex flex-wrap items-center justify-between gap-2">
              {/* `flex-wrap` and `min-w-0`: four items (title, name, verdict, date) run out of room
                  at phone width and would otherwise break mid-phrase rather than onto clean lines. */}
              <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                <span className="text-xs font-medium text-zinc-700">{approver.title}</span>
                <span className="text-xs text-zinc-500">{approver.decidedByName ?? approver.name}</span>
                <DecisionPill decision={approver.decision} />
                {approver.decidedAt && (
                  <span className="text-xs text-zinc-500">{shortDate(approver.decidedAt)}</span>
                )}
              </div>
              {canDecide && (
                <ApprovalControls
                  approver={approver}
                  isSelf={isSelf}
                  isDelegate={viewer.delegateKeys.includes(approver.key)}
                  disabled={busy}
                  onDecide={(key, decision, reason) => {
                    // The CEO's own approval carries the rating on screen — see `PriorityPanel`.
                    const carriesRating =
                      key === PRIORITY_OWNER_KEY && decision === 'Approved' && viewer.canSetPriority;
                    if (!carriesRating) return onDecide(proposal.id, key, decision, reason);
                    onDecide(proposal.id, key, decision, reason, draftRating);
                    setPickedRating(undefined);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {transferred && (
        <p className="mt-3 text-xs text-emerald-700">
          In the hiring plan as a <strong>Backlog</strong> role. Its approvals are now history and
          cannot be changed here.
        </p>
      )}

      {transferPending && (
        <div className={`${RADIUS} mt-3 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800`}>
          <p>
            Approved, but the role is <strong>not yet in the hiring plan</strong>. Until it is,
            nobody planning against the plan can see it.
          </p>
          {canFinishTransfer && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              disabled={busy}
              onClick={() => onRetryTransfer(proposal.id)}
            >
              Add it to the hiring plan
            </Button>
          )}
        </div>
      )}

      {proposal.approvalLog && (
        <div className="mt-3">
          <button
            type="button"
            aria-expanded={showLog}
            // `zinc-500`, not the `zinc-400` this page uses for static footnotes: it is the only
            // clickable thing on the card, and 400 on white is under 3:1.
            className="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700"
            onClick={() => setShowLog((open) => !open)}
          >
            {showLog ? 'Hide' : 'Show'} decision history
          </button>
          {showLog && (
            <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-zinc-600">
              {proposal.approvalLog}
            </pre>
          )}
        </div>
      )}
    </article>
  );
}
